import { useRef, useState } from 'react';

import BpkAutosuggest, {
  BpkAutosuggestSuggestion,
} from '@skyscanner-internal/backpack-web/bpk-component-autosuggest';
import CityIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/city';
import FlagIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flag';
import { cssModules } from '@skyscanner-internal/backpack-web/bpk-react-utils';
import {
  filterOriginCities,
  getFlightPriceMapCityUrl,
  getFlightPriceMapCountryUrl,
  matchesCountryName,
} from '@web-platform/travel-tools-flight-price-map-utils-frontend';
import { useLocation } from 'react-router-dom';

import { OriginInput } from './OriginInput/OriginInput';

import type { BpkInputRenderProps } from '@skyscanner-internal/backpack-web/bpk-component-autosuggest/src/BpkAutosuggestV2/BpkAutosuggest';
import type { CityItem } from '@web-platform/travel-tools-flight-price-map-utils-frontend';

import STYLES from './OriginAutosuggest.module.scss';

type CountryItem = { type: 'country'; name: string };
type SuggestionItem = CityItem | CountryItem;

const isCountryItem = (item: SuggestionItem): item is CountryItem =>
  'type' in item && item.type === 'country';

const cls = cssModules(STYLES);

type OriginAutosuggestProps = {
  cities: CityItem[];
  countryName?: string;
  countrySlug: string;
  inputPlaceholder?: string;
  isMobile?: boolean;
  autoFocus?: boolean;
  onClose?: () => void;
  onSelect: (city: CityItem) => void;
};

export const OriginAutosuggest = ({
  autoFocus = true,
  cities,
  countryName,
  countrySlug,
  inputPlaceholder,
  isMobile = false,
  onClose,
  onSelect,
}: OriginAutosuggestProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const { search } = useLocation();

  const countryItem: CountryItem | null = countryName
    ? { type: 'country', name: countryName }
    : null;
  const filteredCities = filterOriginCities({ cities, searchQuery, countryName });
  const suggestions: SuggestionItem[] =
    countryItem && (!searchQuery.trim() || matchesCountryName(searchQuery, countryName))
      ? [countryItem, ...filteredCities]
      : filteredCities;

  const renderSuggestion = (item: SuggestionItem) => {
    if (isCountryItem(item)) {
      return (
        <a className={cls('suggestionLink')} href={getFlightPriceMapCountryUrl(search)}>
          <BpkAutosuggestSuggestion
            className={STYLES.autosuggestSuggestion}
            icon={FlagIcon}
            value={item.name}
          />
        </a>
      );
    }

    return (
      <a
        className={cls('suggestionLink')}
        href={getFlightPriceMapCityUrl(countrySlug, item.entityId, search)}
      >
        <BpkAutosuggestSuggestion
          className={STYLES.autosuggestSuggestion}
          icon={CityIcon}
          subHeading={countryName}
          value={item.name}
        />
      </a>
    );
  };

  const handleSelected = (data?: { suggestion?: SuggestionItem }) => {
    if (!data?.suggestion) return;
    if (isCountryItem(data.suggestion)) {
      onClose?.();
    } else {
      onSelect(data.suggestion);
    }
  };

  const renderInputComponent = (props: BpkInputRenderProps) => (
    <OriginInput
      {...props}
      placeholder={inputPlaceholder}
      searchQuery={searchQuery}
      onClearQuery={() => setSearchQuery('')}
    />
  );

  return (
    <div className={cls('container')} data-testid="origin-autosuggest" ref={containerRef}>
      <BpkAutosuggest
        alwaysRenderSuggestions
        getSuggestionValue={(item: SuggestionItem) => item.name}
        id="travel-confidence-map-origin-autosuggest"
        inputProps={{ autoFocus, large: true }}
        isDesktop={false}
        onSuggestionSelected={handleSelected}
        onSuggestionsClearRequested={() => setSearchQuery('')}
        onSuggestionsFetchRequested={setSearchQuery}
        renderInputComponent={isMobile ? renderInputComponent : undefined}
        renderSuggestion={renderSuggestion}
        showClear
        suggestions={suggestions}
        theme={{ ...STYLES }}
      />
    </div>
  );
};
